import { Form, Link, useLocation } from "@remix-run/react";
import { CircleUser, Menu, Package2 } from "lucide-react"
import * as React from "react";
import { Sheet, SheetContent, SheetTrigger } from "~/components/ui/sheet"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu"
import { Button } from "~/components/ui/button"
import { useUser } from "~/utils/hooks/useUser";
import SearchBar from "./searchbar2";
import { urlPattern } from "~/utils/urlPattern";
import { useABTest } from "~/utils/hooks/useABTest";
import { trackABTestConversion } from "~/utils/abTesting";

const popularCities = [
  { name: "Madrid", slug: "madrid" },
  { name: "Barcelona", slug: "barcelona" },
  { name: "Valencia", slug: "valencia" }, 
  { name: "Sevilla", slug: "sevilla" },
  { name: "Málaga", slug: "malaga" }
];


export default function Header() {
  const location = useLocation();
  const { userId, isAdmin } = useUser();
  const [isOpen, setIsOpen] = React.useState(false);
  
  // A/B Test: Public workspace link
  const { isVariant: showPublicWorkspaceLink, variant } = useABTest('publicWorkspaceLink');

  const shouldHideSearch = location.pathname.startsWith('/cuenta') || location.pathname.startsWith('/admin');

  // Function to close the mobile menu
  const closeMenu = () => {
    setIsOpen(false);
  };
  
  // Track A/B test conversion when link is clicked
  const handlePublicWorkspaceLinkClick = () => {
    trackABTestConversion('publicWorkspaceLink', variant, 'navbar_link_click');
  };

  return (
    <header className="sticky top-0 z-50 flex flex-col border-b bg-white">
    <div className="flex w-full h-16 items-center px-4 md:px-6 relative">
      {/* Logo - maintained size for brand recognition */}
      <Link
        to="/"
        className="absolute hidden md:flex items-center gap-3 shrink-0"
      >
        <img 
          src="https://s3.eu-west-3.amazonaws.com/assets.compartirespacios.com/workspace/compartir-pisos-logo1.png" 
          alt="Compartir Espacios"
          className="h-8 w-auto"
        />
        <span className="sr-only">Compartir Espacios</span>
      </Link>

      {/* Main Navigation - Simplified typography */}
      <nav className="hidden w-full flex-col md:flex md:flex-row justify-center items-center gap-6">
        {/* {popularCities.map((city) => (
          <Link
          key={city.slug}
          to={urlPattern.build({ city: city.slug, })}
          className={cn(
            "text-base font-normal transition-colors",
            isCityActive(city.slug) 
              ? "text-foreground underline underline-offset-4" 
              : "text-foreground/80 hover:text-foreground"
          )}
          >
            {city.name}
          </Link>
        ))} */}
      </nav>

      {/* Mobile Menu - Consistent styling */}
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setIsOpen(true)}
          >
            <Menu className="h-5 w-5" />
            <span className="sr-only">Menú de navegación</span>
          </Button>
        </SheetTrigger>
        <SheetContent side="left">
          <nav className="flex flex-col gap-6">
            <Link to="/" className="flex items-center gap-2" onClick={closeMenu}>
              <Package2 className="h-6 w-6" />
              <span className="text-lg font-medium">Compartir Espacios</span>
            </Link>
            {/* Consistent text sizing in mobile menu */}
            <div className="flex flex-col gap-4">
              {popularCities.map((city) => (
                <Link
                  key={city.slug}
                  to={urlPattern.build({ city: city.slug, })}
                  className="text-base text-foreground/80 hover:text-foreground transition-colors"
                  onClick={closeMenu}
                >
                  {city.name}
                </Link>
              ))}
            </div>

            {/* Separator */}
            <div className="h-px bg-black" />

            {/* Additional Links */}
            <div className="flex flex-col gap-4">
              {/* A/B Test: Public workspace creation link */}
              {showPublicWorkspaceLink && (
                <Link
                  to="/crear-espacio-publico"
                  className="text-base text-foreground/80 hover:text-foreground transition-colors underline underline-offset-4 decoration-foreground/30 hover:decoration-foreground"
                  onClick={() => {
                    handlePublicWorkspaceLinkClick();
                    closeMenu();
                  }}
                >
                  Publica tu espacio
                </Link>
              )}
              
              <Link
                to="/solicitar-espacio"
                className="text-base text-foreground/80 hover:text-foreground transition-colors underline underline-offset-4 decoration-foreground/30 hover:decoration-foreground"
                onClick={closeMenu}
              >
                Solicita un espacio
              </Link>
            </div>
          </nav>
        </SheetContent>
      </Sheet>

      {/* Right side actions - Improved alignment and spacing */}
      <div className="absolute right-4 flex items-center gap-4">
        {/* A/B Test: Public workspace creation link */}
        {showPublicWorkspaceLink && (
          <Link
            to="/crear-espacio-publico"
            className="hidden md:block text-sm font-normal text-foreground/80 hover:text-foreground transition-colors underline underline-offset-4 decoration-foreground/30 hover:decoration-foreground"
            onClick={handlePublicWorkspaceLinkClick}
          >
            Publica tu espacio
          </Link>
        )}
        
        <Link
          to="/solicitar-espacio"
          className="hidden md:block text-sm font-normal text-foreground/80 hover:text-foreground transition-colors underline underline-offset-4 decoration-foreground/30 hover:decoration-foreground"
        >
          Solicita un espacio
        </Link>

        {userId ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full">
                <CircleUser className="h-5 w-5" />
                <span className="sr-only">Menú de usuario</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-sm font-medium">Mi Cuenta</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {/* Consistent menu item styling */}
              <DropdownMenuItem className="text-sm">
                <Link to="/cuenta" className="w-full">Perfil</Link>
              </DropdownMenuItem>
              <DropdownMenuItem className="text-sm">
                <Link to="/cuenta/espacios" className="w-full">Mis Espacios</Link>
              </DropdownMenuItem>
              <DropdownMenuItem className="text-sm">
                <Link to="/cuenta/suscripcion" className="w-full">Suscripción</Link>
              </DropdownMenuItem>
              {isAdmin && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-sm">
                    <Link to="/admin" className="w-full">Panel de Admin</Link>
                  </DropdownMenuItem>
                </>
              )}
              <DropdownMenuSeparator />
              <Form action="/logout" method="POST" className="w-full">
                <Button variant="ghost" size="sm" className="w-full text-sm justify-start">
                  Cerrar sesión
                </Button>
              </Form>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <Button variant="ghost" size="icon" className="rounded-full">
            <Link to="/login">
              <CircleUser className="h-5 w-5" />
            </Link>
          </Button>
        )}
      </div>
    </div>

    {/* Search Bar */}
    {!shouldHideSearch && (
      <div className="">
        <SearchBar />
      </div>
    )}
  </header>
  )
}
